import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'wasp/client/operations';
import { getOccasionBySlug } from 'wasp/client/operations';
import { DashboardLayout } from '../../client/components/Sidebar';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Info,
  Sparkles,
  CalendarDays,
  Building,
  Loader,
} from 'lucide-react';
type OccasionWithRelations = {
  id: string;
  name: string;
  slug: string;
  occasionTypeId: string;
  communityMonth: number | null;
  communityDay: number | null;
  durationDays: number | null;
  startDate: Date | null;
  endDate: Date | null;
  description: string | null;
  significance: string | null;
  imageUrl: string | null;
  isActive: boolean;
  occasionType: {
    id: string;
    key: string;
    label: string;
    description: string | null;
    icon: string | null;
  };
  locationOccasions?: Array<{
    id: string;
    location: {
      id: string;
      name: string;
      slug: string | null;
    };
  }>;
  events?: Array<{
    id: string;
    title: string;
    startTime: Date;
    location: {
      id: string;
      name: string;
      slug: string | null;
    };
    organizer: {
      id: string;
      username: string | null;
      fullName: string | null;
    };
  }>;
};

export default function OccasionDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const {
    data: occasion,
    isLoading,
    error,
  } = useQuery(getOccasionBySlug, { slug: slug! }) as {
    data: OccasionWithRelations | null;
    isLoading: boolean;
    error: any;
  };

  const formatDate = (date: Date | null | string) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatEventTime = (date: Date | string) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getcommunityMonthName = (month: number) => {
    const months = [
      'Muharram',
      'Safar',
      'Rabi al-Awwal',
      'Rabi al-Thani',
      'Jumada al-Awwal',
      'Jumada al-Thani',
      'Rajab',
      'Shaban',
      'Ramadan',
      'Shawwal',
      'Dhul Qadah',
      'Dhul Hijjah',
    ];
    return months[month - 1] || '';
  };

  const isActive = (occ: any) => {
    if (!occ.startDate || !occ.endDate) return false;
    const now = new Date();
    return now >= new Date(occ.startDate) && now <= new Date(occ.endDate);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-indigo-600" />
          <span className="ml-3 text-gray-600 dark:text-gray-300">
            Loading occasion details...
          </span>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !occasion) {
    return (
      <DashboardLayout>
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">
              {error ? `Error: ${(error as any).message}` : 'Occasion not found'}
            </p>
          </div>
          <button
            onClick={() => navigate('/occasions')}
            className="mt-4 text-indigo-600 hover:text-indigo-700 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Occasions
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/occasions')}
          className="mb-6 text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Occasions
        </button>

        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 mb-6">
          {/* Status Badge */}
          {isActive(occasion) && (
            <span className="inline-block mb-4 px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded-full">
              Active Now
            </span>
          )}

          {/* Occasion Type */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-3xl">{occasion.occasionType.icon}</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {occasion.occasionType.label}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
            {occasion.name}
          </h1>

          {/* Community Date */}
          {occasion.communityMonth && (
            <div className="flex items-center gap-3 text-lg text-gray-700 dark:text-gray-300 mb-3">
              <Calendar className="w-5 h-5 text-indigo-600" />
              <span>
                {getcommunityMonthName(occasion.communityMonth)}
                {occasion.communityDay && ` ${occasion.communityDay}`}
                {occasion.durationDays && occasion.durationDays > 1 && (
                  <span className="text-sm text-gray-500 ml-2">
                    ({occasion.durationDays} days)
                  </span>
                )}
              </span>
            </div>
          )}

          {/* Gregorian Dates */}
          {occasion.startDate && (
            <div className="flex items-center gap-3 text-lg text-gray-700 dark:text-gray-300">
              <Clock className="w-5 h-5 text-indigo-600" />
              <span>
                {formatDate(occasion.startDate)}
                {occasion.endDate &&
                  new Date(occasion.startDate).toDateString() !==
                    new Date(occasion.endDate).toDateString() && (
                    <> - {formatDate(occasion.endDate)}</>
                  )}
              </span>
            </div>
          )}
        </div>

        {/* Description */}
        {occasion.description && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Info className="w-5 h-5 text-indigo-600" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Description
              </h2>
            </div>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {occasion.description}
            </p>
          </div>
        )}

        {/* Significance */}
        {occasion.significance && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-indigo-600" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Religious Significance
              </h2>
            </div>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {occasion.significance}
            </p>
          </div>
        )}

        {/* Locations Celebrating */}
        {occasion.locationOccasions && occasion.locationOccasions.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Building className="w-5 h-5 text-indigo-600" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Celebrating Locations ({occasion.locationOccasions.length})
              </h2>
            </div>
            <div className="space-y-3">
              {occasion.locationOccasions.map((locOcc) => (
                <Link
                  key={locOcc.id}
                  to={`/locations/${locOcc.location.slug}`}
                  className="block p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    <span className="font-medium text-gray-900 dark:text-white">
                      {locOcc.location.name}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Related Events */}
        {occasion.events && occasion.events.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays className="w-5 h-5 text-indigo-600" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Upcoming Events ({occasion.events.length})
              </h2>
            </div>
            <div className="space-y-3">
              {occasion.events.map((event) => (
                <Link
                  key={event.id}
                  to={`/locations/${event.location.slug}/events/${event.id}`}
                  className="block p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    {event.title}
                  </h3>
                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {event.location.name}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatDate(event.startTime)} at {formatEventTime(event.startTime)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}



